import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Container, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBalanceScale,
  faUsers,
  faBullseye,
  faLightbulb,
  faFire,
  faHandshake,
  faRocket,
  faGavel,
  faTrophy,
  faHourglassHalf,
  faPassport,
  faCarBurst,
  faCar,
  faBriefcase,
  faShieldHalved,
  faHandHoldingHeart,
  faIdCard,
} from '@fortawesome/free-solid-svg-icons';

import './homePage.scss';
import logon from '../../assets/homePage/brand-logo.png';
import SEOUpdater from '../../utils/hoc/SEOUpdater';
import TestimonialsSection from '../../components/testimonials';
import { withLang } from '../../utils/withLang';

const principleIcons = [
  faBalanceScale,
  faUsers,
  faBullseye,
  faLightbulb,
  faFire,
  faHandshake,
  faRocket,
  faGavel,
  faTrophy,
  faHourglassHalf,
];

const practiceAreaKeys = [
  { key: 'immigration', icon: faPassport },
  { key: 'personalInjury', icon: faCarBurst },
  { key: 'lemonLaw', icon: faCar },
  { key: 'employmentLaw', icon: faBriefcase },
];

// Removal Defense / Asylum Assistance / Citizenship & Visas: these three
// cards (removalTitle/removalText, asylumTitle/asylumText,
// citizenshipTitle/citizenshipText) plus servicesTitle ("Exceptional Team
// for Exceptional Results") were written and translated into all 4
// languages in homePage.json from the start, but no section of this page
// ever rendered them - a 5 Aug 2026 SEO audit caught this: 13 of the 31
// keys in this file were never referenced in the shipped code, these
// among them. Immigration is the firm's lead practice (see heroTitle) and
// these are immigration sub-specialties, so this section sits right after
// the main practice-area grid as a deeper look at that one practice area.
const immigrationServiceKeys = [
  { titleKey: 'removalTitle', textKey: 'removalText', icon: faShieldHalved },
  { titleKey: 'asylumTitle', textKey: 'asylumText', icon: faHandHoldingHeart },
  { titleKey: 'citizenshipTitle', textKey: 'citizenshipText', icon: faIdCard },
];

const HomePage = () => {
  const { t } = useTranslation('homePage');
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="homePage">
      <SEOUpdater page="homePage" />

      <section className="hero-section">
        {/*
          heroTitle/heroSubtitle were also written from the start and never
          rendered (see the immigrationServiceKeys comment above) - the hero
          had only this brandName text, kept as a real h1 for a search
          engine but hidden from view. Making the real headline copy the
          visible h1 instead - and dropping brandName to a plain
          visually-hidden accessible label rather than a second h1 - gives
          the homepage an actual, visible statement of what the page is
          about, which is what a 14 Aug 2026 SEO audit flagged as the one
          thing every language edition of this page was missing.
        */}
        <span className="visually-hidden">{t('brandName')}</span>
        <div className="hero-bg" />
        <div className="top-logo">
          {[...Array(3)].map((_, i) => (
            <img
              key={i}
              src={logon}
              alt="Avagyan Law"
              className={`logo logo${i + 1}`}
              width={1210}
              height={408}
            />
          ))}
        </div>
        <Container className="text-center">
          <h1 className="hero-title">{t('heroTitle')}</h1>
          <div className="hero-divider" />
          <p className="hero-subtitle">{t('heroSubtitle')}</p>
        </Container>
      </section>

      <section className="philosophy-section mb-5">
        <div className="overlay">
          <Container className="text-center text-white">
            <h2 className="section-title">{t('philosophyTitle')}</h2>
            <div className="divider" />
            <p className="section-text">{t('philosophyParagraph1')}</p>
            <p className="section-text">{t('philosophyParagraph2')}</p>
          </Container>
        </div>
      </section>

      <section className="services-section text-center">
        <Container>
          <h2 className="section-title">{t('practiceAreasTitle')}</h2>
          <div className="section-divider mx-auto mb-4" />
          <p className="practice-areas-subtitle mx-auto mb-5">{t('practiceAreasSubtitle')}</p>
          <Row>
            {practiceAreaKeys.map(({ key, icon }) => (
              <Col md={6} lg={3} key={key} className="mb-4">
                <div className="service-card p-4 h-100">
                  <div className="icon-wrapper">
                    <FontAwesomeIcon icon={icon} className="service-icon" />
                  </div>
                  <h3 className="card-title">{t(`practiceAreas.${key}.title`)}</h3>
                  <p className="card-text">{t(`practiceAreas.${key}.text`)}</p>
                  <Link
                    to={withLang(location.pathname, t(`practiceAreas.${key}.link`))}
                    className="service-card-link"
                  >
                    {t(`practiceAreas.${key}.linkText`)}
                  </Link>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="services-section text-center immigration-focus-section">
        <Container>
          <h2 className="section-title">{t('servicesTitle')}</h2>
          <div className="section-divider mx-auto mb-4" />
          <Row>
            {immigrationServiceKeys.map(({ titleKey, textKey, icon }) => (
              <Col md={6} lg={4} key={titleKey} className="mb-4">
                <div className="service-card p-4 h-100">
                  <div className="icon-wrapper">
                    <FontAwesomeIcon icon={icon} className="service-icon" />
                  </div>
                  <h3 className="card-title">{t(titleKey)}</h3>
                  <p className="card-text">{t(textKey)}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="why-section">
        <div className="overlay">
          <Container>
            <h2 className="section-title text-center">{t('whyTitle')}</h2>
            <div className="section-divider mx-auto" />
            <Row className="text-center mt-5">
              {principleIcons.map((icon, index) => (
                <Col xs={6} key={index} className="mb-4">
                  <div className="icon-circle">
                    <FontAwesomeIcon icon={icon} size="2x" />
                  </div>
                  <p className="icon-label">{t(`principles.p${index + 1}`)}</p>
                </Col>
              ))}
            </Row>
          </Container>
        </div>
      </section>

      <TestimonialsSection />
    </div>
  );
};

export default HomePage;
